import React from 'react'
import { useEffect, useState } from 'react'
import styles from './style.module.scss'
import { useTranslation } from 'react-i18next'

import * as calendarService from 'pages/calendar/modules/CalendarService'
// 순환 참조 방지를 위해 EventManager 대신 공유 유틸리티에서 import
import { isPersonaEmail, getDisplayEmailFromPersonaEmail } from 'utils/personaEmail'
import { isOrgSharedAccountEmail } from 'utils/orgSharedAccount'
import useOrgSharedOwnerName from '../../hooks/useOrgSharedOwnerName'

import reminderIcon from 'assets/images/calendar/reminder-icon.svg'
import { validateEmail } from 'pages/contacts/utils'
import orgChartHelper from 'pages/contacts/components/OrgChart/Helper'

import {
  addNotificationListener,
  removeNotificationListener,
} from 'pages/calendar/modules/CalendarState'

interface CalendarInvitation {
  isReply: boolean
  summary: string
  organizer: string
  accept: boolean
  replyUrl: string
  folderType: 'calendar' | 'todo'
  notiUrl: string
}

interface InvitationListItemProps {
  notification: CalendarInvitation
}

const InvitationListItem = ({ notification }: InvitationListItemProps) => {
  const { isReply, summary, organizer, accept, replyUrl } = notification
  const { t } = useTranslation()

  const [title, setTitle] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const organizerOuName = useOrgSharedOwnerName(organizer)

  if (isReply) {
    useEffect(() => {
      setTitle(summary)

      const parts = replyUrl.split('/')
      let email = parts[parts.length - 2]

      if (validateEmail(email)) {
        if (isPersonaEmail(email)) {
          email = getDisplayEmailFromPersonaEmail(email)
        }
        orgChartHelper.findContact(email).then((contact: any) => {
          let value = null
          if (contact) {
            value = contact.name + ' (' + email + ')'
          } else {
            value = email
          }
          if (value) {
            setMessage(
              accept
                ? notification.folderType === 'calendar'
                  ? t('calendar.edit.joinedSharedCalendarWho', { name: value })
                  : t('calendar.edit.joinedSharedTodoWho', { name: value })
                : notification.folderType === 'calendar'
                  ? t('calendar.edit.declinedSharedCalendarWho', { name: value })
                  : t('calendar.edit.declinedSharedTodoWho', { name: value }),
            )
          }
        })
      }

      setMessage(
        accept
          ? notification.folderType === 'calendar'
            ? t('calendar.edit.joinedSharedCalendar')
            : t('calendar.edit.joinedSharedTodo')
          : notification.folderType === 'calendar'
            ? t('calendar.edit.declinedSharedCalendar')
            : t('calendar.edit.declinedSharedTodo'),
      )
    }, [replyUrl, accept, notification.folderType, summary, t])
  } else {
    useEffect(() => {
      setTitle(`${t('common.button.join')} "${summary}"`)
      let organizerEmail = organizer
      if (isPersonaEmail(organizer)) {
        organizerEmail = getDisplayEmailFromPersonaEmail(organizer)
      }

      if (isOrgSharedAccountEmail(organizerEmail)) {
        setMessage(
          `${organizerOuName ?? t('calendar.share.orgSharedAccount')} ${
            notification.folderType === 'calendar'
              ? t('calendar.edit.inviteShared')
              : t('calendar.edit.inviteSharedTodo')
          }`,
        )
        return
      }

      orgChartHelper.findContact(organizerEmail).then((contact: any) => {
        let value = null
        if (contact) {
          value = contact.name + ' (' + organizerEmail + ')'
        } else {
          value = organizerEmail
        }
        setMessage(
          `${value} ${
            notification.folderType === 'calendar'
              ? t('calendar.edit.inviteShared')
              : t('calendar.edit.inviteSharedTodo')
          }`,
        )
      })
    }, [summary, organizer, notification.folderType, t, organizerOuName])
  }

  return (
    <div className={styles.listItem}>
      <div className={styles.title}>{title}</div>
      <div className={styles.message}>{message}</div>
      {!isReply && (
        <div className={styles.actions}>
          <button
            className={styles.joinButton}
            onClick={() => calendarService.responseNotification(notification, true)}
          >
            {t('common.button.join')}
          </button>
          <button
            className={styles.declineButton}
            onClick={() => calendarService.responseNotification(notification, false)}
          >
            {t('common.button.decline')}
          </button>
        </div>
      )}
      {isReply && (
        <div className={styles.actions}>
          <button
            className={styles.joinButton}
            onClick={() => calendarService.deleteNotification(notification.notiUrl)}
          >
            {t('common.button.ok')}
          </button>
        </div>
      )}
    </div>
  )
}

interface InvitationListProps {
  folderType?: 'all' | 'calendar' | 'todo'
  onClose: () => void
}

const InvitationList = ({ folderType = 'all', onClose }: InvitationListProps) => {
  const { t } = useTranslation()

  const [notifications, setNotifications] = useState<CalendarInvitation[]>([])

  const updateNotifications = () => {
    let ns = calendarService.getNotifications() as unknown as CalendarInvitation[]

    if (folderType !== null && folderType !== 'all') {
      ns = ns.filter((notification: CalendarInvitation) => notification.folderType === folderType)
    }
    setNotifications(ns)
  }

  useEffect(() => {
    updateNotifications()

    const delayedUpdate = () => {
      setTimeout(() => {
        updateNotifications()
      }, 10)
    }

    addNotificationListener(delayedUpdate)
    return () => {
      removeNotificationListener(delayedUpdate)
    }
  }, [folderType])

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>{t('calendar.edit.invitation')}</h2>
      </div>

      <div className={styles.content}>
        {notifications.length === 0 ? (
          <div className={styles.noInvitations}>
            <img src={reminderIcon} alt="No invitations" />
            {t('calendar.edit.noInvitation')}
          </div>
        ) : (
          <div className={styles.list}>
            {notifications.map((notification, index) => (
              <InvitationListItem key={index} notification={notification} />
            ))}
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <button className={styles.closeButton} onClick={onClose}>
          {t('common.button.close')}
        </button>
      </div>
    </div>
  )
}

export default InvitationList
